import { z } from "zod";

import {DumpTruckFields, DumpTruckFilterFields, DumpTruckSummaryFields} from "./fields";
import { EntityWithIdFields } from "../../../types";
import { DumpTruckStatusTypes, DumpTruckTypeTypes } from "./enum";

export const schemaDumpTruckBase = z.object({
    [DumpTruckFields.Client]: z.string(),
    [DumpTruckFields.Address]: z.string().nullable(),
    [DumpTruckFields.Observations]: z.string().nullable(),
    [DumpTruckFields.Amount]: z.number().nullable(),
    [DumpTruckFields.Invoice]: z.string().nullable(),
    [DumpTruckFields.DeliveryNote]: z.string().nullable(),

    [DumpTruckFields.TypeCode]: z.nativeEnum(DumpTruckTypeTypes),
    [DumpTruckFields.TypeDescription]: z.string(),

    [DumpTruckFields.StatusCode]: z.nativeEnum(DumpTruckStatusTypes),
    [DumpTruckFields.StatusDescription]: z.string(),

    [DumpTruckFields.CreationDate]: z.coerce.date().nullable(),
    [DumpTruckFields.DeliveryDate]: z.coerce.date().nullable(),
    [DumpTruckFields.RemovalDate]: z.coerce.date().nullable(),

    [DumpTruckFields.CreatedByUserId]: z.number(),
});

export const schemaDumpTruckView = schemaDumpTruckBase.extend({
    [EntityWithIdFields.Id]: z.number(),
});
export type DumpTruckViewDTO = z.infer<typeof schemaDumpTruckView>;


export const schemaDumpTruckInsert = z.object({
    [DumpTruckFields.Client]: z.string().min(1, 'Client is required'),
    [DumpTruckFields.Address]: z.string().nullable(),
    [DumpTruckFields.Observations]: z.string().nullable(),
    [DumpTruckFields.Amount]: z.string().nullable().optional(),
    [DumpTruckFields.Invoice]: z.string().nullable(),
    [DumpTruckFields.DeliveryNote]: z.string().nullable(),

    [DumpTruckFields.TypeCode]: z.nativeEnum(DumpTruckTypeTypes),
    [DumpTruckFields.StatusCode]: z.nativeEnum(DumpTruckStatusTypes),

    [DumpTruckFields.DeliveryDate]: z.coerce.date().nullable(),
    [DumpTruckFields.RemovalDate]: z.coerce.date().nullable(),

    [DumpTruckFields.Quantity]: z.preprocess(
        (value) => {
            const parsed = Number(value);

            return isNaN(parsed) ? undefined : parsed;
        },
        z.number({ invalid_type_error: 'Debe ser un número' })
            .min(1, 'Debe ser al menos 1')
            .default(1)
    ),
});
export type DumpTruckInsertDTO = z.infer<typeof schemaDumpTruckInsert>;


export const schemaDumpTruckStatusOption = z.object({
    [DumpTruckFields.StatusCode]: z.nativeEnum(DumpTruckStatusTypes),
    [DumpTruckFields.StatusDescription]: z.string(),
});
export type DumpTruckStatusOption = z.infer<typeof schemaDumpTruckStatusOption>;

export const schemaDumpTruckTypeOption = z.object({
    [DumpTruckFields.TypeCode]: z.nativeEnum(DumpTruckTypeTypes),
    [DumpTruckFields.TypeDescription]: z.string(),
});
export type DumpTruckTypeOption = z.infer<typeof schemaDumpTruckTypeOption>;


export const schemaDumpTruckFilter = z.object({
    [DumpTruckFilterFields.Client]: z.string().optional(),
    [DumpTruckFilterFields.Address]: z.string().optional(),
    [DumpTruckFilterFields.Invoice]: z.string().optional(),
    [DumpTruckFilterFields.DeliveryNote]: z.string().optional(),
    [DumpTruckFilterFields.DeliveryDateStart]: z.date().optional().nullable(),
    [DumpTruckFilterFields.DeliveryDateEnd]: z.date().optional().nullable(),
    [DumpTruckFilterFields.RemovalDateStart]: z.date().optional().nullable(),
    [DumpTruckFilterFields.RemovalDateEnd]: z.date().optional().nullable(),
    [DumpTruckFilterFields.IsBilled]: z.preprocess((value) => {
        if (value === -1) {
            return null;
        }
        if (value === 1) {
            return true;
        }
        if (value === 2) {
            return false;
        }

        return value;
    }, z.boolean().optional().nullable()),
    [DumpTruckFilterFields.TypeCodeList]: z.array(z.number()).optional(),
    [DumpTruckFilterFields.StatusCodeList]: z.array(z.number()).optional(),
});
export type DumpTruckFilterDTO = z.infer<typeof schemaDumpTruckFilter>;


export const schemaDumpTruckUpdate = z.object({
    [DumpTruckFields.Client]: z.string(),
    [DumpTruckFields.Address]: z.string().nullable(),
    [DumpTruckFields.Observations]: z.string().nullable(),
    [DumpTruckFields.Amount]: z.number().nullable(),
    [DumpTruckFields.DeliveryNote]: z.string().nullable(),

    [DumpTruckFields.TypeCode]: z.nativeEnum(DumpTruckTypeTypes),
    [DumpTruckFields.StatusCode]: z.nativeEnum(DumpTruckStatusTypes),

    [DumpTruckFields.DeliveryDate]: z.coerce.date().nullable(),
    [DumpTruckFields.RemovalDate]: z.coerce.date().nullable(),
});

export type DumpTruckUpdateDTO = z.infer<typeof schemaDumpTruckUpdate>;

export const schemaDumpTruckSummary = z.object({
    [DumpTruckSummaryFields.ActiveQuantity]: z.number(),
    [DumpTruckSummaryFields.AvailableQuantity]: z.number(),
    [DumpTruckSummaryFields.DeliveredCurrentMonth]: z.number(),
    [DumpTruckSummaryFields.DeliveredPreviousMonth]: z.number(),
    [DumpTruckSummaryFields.UnbilledQuantity]: z.number(),
})

export type DumpTruckSummarySummary = z.infer<typeof schemaDumpTruckSummary>;